"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Eraser, LoaderCircle, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  bakeRedactedImageFile,
  canRedactImageFile,
  normalizeRedactionBox,
  REDACTION_STYLE_OPTIONS,
  type RedactionBox,
  type RedactionStyle,
} from "@/lib/media/redact-image";
import { cn, createId, getErrorMessage } from "@/lib/utils";

type DragMode =
  | { kind: "move"; id: string; startX: number; startY: number; orig: RedactionBox }
  | {
      kind: "resize";
      id: string;
      corner: "nw" | "ne" | "sw" | "se";
      startX: number;
      startY: number;
      orig: RedactionBox;
    }
  | { kind: "create"; startX: number; startY: number }
  | null;

type ImageRedactionEditorProps = {
  open: boolean;
  file: File | null;
  onOpenChange: (open: boolean) => void;
  onApply: (file: File) => void;
};

export function ImageRedactionEditor({
  open,
  file,
  onOpenChange,
  onApply,
}: ImageRedactionEditorProps) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [boxes, setBoxes] = useState<RedactionBox[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [style, setStyle] = useState<RedactionStyle>("solid");
  const [drag, setDrag] = useState<DragMode>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [drawMode, setDrawMode] = useState(false);

  useEffect(() => {
    if (!open || !file) {
      setObjectUrl(null);
      setBoxes([]);
      setSelectedId(null);
      setDrag(null);
      setError(null);
      setDrawMode(false);
      return;
    }
    if (!canRedactImageFile(file)) {
      setError("Csak JPG, PNG vagy WebP képet lehet kitakarni.");
      return;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    setBoxes([]);
    setSelectedId(null);
    setError(null);
    return () => URL.revokeObjectURL(url);
  }, [file, open]);

  const selected = useMemo(
    () => boxes.find((box) => box.id === selectedId) ?? null,
    [boxes, selectedId],
  );

  const relativePoint = useCallback((clientX: number, clientY: number) => {
    const stage = stageRef.current;
    if (!stage) {
      return { x: 0, y: 0 };
    }
    const rect = stage.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  }, []);

  function addCenteredBox() {
    const box = normalizeRedactionBox({
      id: createId(),
      x: 0.3,
      y: 0.3,
      w: 0.4,
      h: 0.2,
      style,
    });
    setBoxes((current) => [...current, box]);
    setSelectedId(box.id);
  }

  function removeSelected() {
    if (!selectedId) {
      return;
    }
    setBoxes((current) => current.filter((box) => box.id !== selectedId));
    setSelectedId(null);
  }

  function onStagePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) {
      return;
    }
    const point = relativePoint(event.clientX, event.clientY);
    if (drawMode) {
      event.currentTarget.setPointerCapture(event.pointerId);
      setDrag({ kind: "create", startX: point.x, startY: point.y });
      const draftId = createId();
      const draft = normalizeRedactionBox({
        id: draftId,
        x: point.x,
        y: point.y,
        w: 0.02,
        h: 0.02,
        style,
      });
      setBoxes((current) => [...current, draft]);
      setSelectedId(draftId);
      return;
    }
    setSelectedId(null);
  }

  function onBoxPointerDown(
    event: ReactPointerEvent<HTMLDivElement>,
    box: RedactionBox,
  ) {
    if (event.button !== 0 || drawMode) {
      return;
    }
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = relativePoint(event.clientX, event.clientY);
    setSelectedId(box.id);
    setDrag({
      kind: "move",
      id: box.id,
      startX: point.x,
      startY: point.y,
      orig: box,
    });
  }

  function onResizePointerDown(
    event: ReactPointerEvent<HTMLSpanElement>,
    box: RedactionBox,
    corner: "nw" | "ne" | "sw" | "se",
  ) {
    if (event.button !== 0) {
      return;
    }
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = relativePoint(event.clientX, event.clientY);
    setSelectedId(box.id);
    setDrag({
      kind: "resize",
      id: box.id,
      corner,
      startX: point.x,
      startY: point.y,
      orig: box,
    });
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!drag) {
      return;
    }
    const point = relativePoint(event.clientX, event.clientY);

    if (drag.kind === "create" && selectedId) {
      const x = Math.min(drag.startX, point.x);
      const y = Math.min(drag.startY, point.y);
      const w = Math.abs(point.x - drag.startX);
      const h = Math.abs(point.y - drag.startY);
      setBoxes((current) =>
        current.map((box) =>
          box.id === selectedId
            ? normalizeRedactionBox({ ...box, x, y, w, h, style })
            : box,
        ),
      );
      return;
    }

    if (drag.kind === "move") {
      const dx = point.x - drag.startX;
      const dy = point.y - drag.startY;
      const nextX = Math.min(1 - drag.orig.w, Math.max(0, drag.orig.x + dx));
      const nextY = Math.min(1 - drag.orig.h, Math.max(0, drag.orig.y + dy));
      setBoxes((current) =>
        current.map((box) =>
          box.id === drag.id
            ? normalizeRedactionBox({ ...box, x: nextX, y: nextY })
            : box,
        ),
      );
      return;
    }

    if (drag.kind === "resize") {
      const orig = drag.orig;
      let left = orig.x;
      let top = orig.y;
      let right = orig.x + orig.w;
      let bottom = orig.y + orig.h;

      if (drag.corner.includes("w")) {
        left = Math.min(right - 0.02, Math.max(0, point.x));
      }
      if (drag.corner.includes("e")) {
        right = Math.max(left + 0.02, Math.min(1, point.x));
      }
      if (drag.corner.includes("n")) {
        top = Math.min(bottom - 0.02, Math.max(0, point.y));
      }
      if (drag.corner.includes("s")) {
        bottom = Math.max(top + 0.02, Math.min(1, point.y));
      }

      setBoxes((current) =>
        current.map((box) =>
          box.id === drag.id
            ? normalizeRedactionBox({
                ...box,
                x: left,
                y: top,
                w: right - left,
                h: bottom - top,
              })
            : box,
        ),
      );
    }
  }

  function onPointerUp() {
    if (drag?.kind === "create") {
      setDrawMode(false);
    }
    setDrag(null);
  }

  async function handleApply() {
    if (!file) {
      return;
    }
    if (boxes.length === 0) {
      setError("Adj hozzá legalább egy kitakarást, vagy zárd be a szerkesztőt.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const next = await bakeRedactedImageFile({ file, boxes });
      onApply(next);
      onOpenChange(false);
    } catch (cause) {
      setError(getErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[min(96vw,920px)] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Kép kitakarása</DialogTitle>
          <DialogDescription>
            Jelölj ki területeket, válassz stílust, majd alkalmazd. A kitakart
            kép kerül feltöltésre.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {REDACTION_STYLE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setStyle(option.value);
                if (selectedId) {
                  setBoxes((current) =>
                    current.map((box) =>
                      box.id === selectedId
                        ? { ...box, style: option.value }
                        : box,
                    ),
                  );
                }
              }}
              className={cn(
                "rounded-xl border px-3 py-2 text-left transition",
                style === option.value
                  ? "border-primary bg-primary/5"
                  : "hover:bg-muted/50",
              )}
            >
              <p className="text-sm font-medium">{option.label}</p>
              <p className="text-xs text-muted-foreground">
                {option.description}
              </p>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant={drawMode ? "default" : "outline"}
            onClick={() => setDrawMode((current) => !current)}
          >
            <Eraser className="size-4" />
            {drawMode ? "Rajzolás aktív — húzz a képen" : "Terület rajzolása"}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={addCenteredBox}>
            <Plus className="size-4" />
            Box hozzáadása
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!selected}
            onClick={removeSelected}
          >
            <Trash2 className="size-4" />
            Kijelölt törlése
          </Button>
        </div>

        <div className="flex justify-center overflow-hidden rounded-xl border bg-muted/30 p-2">
          {objectUrl ? (
            <div
              ref={stageRef}
              className={cn(
                "relative inline-block max-w-full",
                drawMode ? "cursor-crosshair" : "cursor-default",
              )}
              onPointerDown={onStagePointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                ref={imageRef}
                src={objectUrl}
                alt="Kitakarandó kép"
                className="block max-h-[55vh] max-w-full"
                draggable={false}
              />

              {boxes.map((box) => (
                <div
                  key={box.id}
                  role="button"
                  tabIndex={0}
                  onPointerDown={(event) => onBoxPointerDown(event, box)}
                  className={cn(
                    "absolute border-2",
                    selectedId === box.id
                      ? "border-primary"
                      : "border-white/80",
                    box.style === "solid" && "bg-zinc-900/85",
                    box.style === "mosaic" &&
                      "bg-[repeating-conic-gradient(#111_0%_25%,#666_0%_50%)] bg-[length:12px_12px] opacity-90",
                    box.style === "blur" && "bg-white/25 backdrop-blur-md",
                  )}
                  style={{
                    left: `${box.x * 100}%`,
                    top: `${box.y * 100}%`,
                    width: `${box.w * 100}%`,
                    height: `${box.h * 100}%`,
                  }}
                >
                  {selectedId === box.id
                    ? (["nw", "ne", "sw", "se"] as const).map((corner) => (
                        <span
                          key={corner}
                          onPointerDown={(event) =>
                            onResizePointerDown(event, box, corner)
                          }
                          className={cn(
                            "absolute size-3 rounded-sm border border-white bg-primary",
                            corner === "nw" &&
                              "-left-1.5 -top-1.5 cursor-nwse-resize",
                            corner === "ne" &&
                              "-right-1.5 -top-1.5 cursor-nesw-resize",
                            corner === "sw" &&
                              "-bottom-1.5 -left-1.5 cursor-nesw-resize",
                            corner === "se" &&
                              "-bottom-1.5 -right-1.5 cursor-nwse-resize",
                          )}
                        />
                      ))
                    : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-64 w-full items-center justify-center text-sm text-muted-foreground">
              Nincs betölthető kép.
            </div>
          )}
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Mégse
          </Button>
          <Button type="button" onClick={() => void handleApply()} disabled={saving}>
            {saving ? <LoaderCircle className="size-4 animate-spin" /> : null}
            Kitakarás alkalmazása
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
